import React, { useContext, useState } from "react";
import { Avatar, Grid, Paper, Stack, TableContainer, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { format } from "date-fns";
import _ from "lodash";
import CardHeaderTitle from "../../components/card-header-title/index.jsx";
import AppBarChart from "../../components/chart/bar.jsx";
import AppPieChart from "../../components/chart/pie.jsx";
import WPMChartComponent from "../../components/chart/wpm-chart.js";
import { AppDataContext } from "../../components/context/app-data-context.jsx";
import InfoCardBox from "../../components/info-card-box/index.js";
import ProgressTable from "../../components/progress-table/index.jsx";
import AppSeeMoreLess from "../../components/see-more-less/index.jsx";
import { useThemeMode } from "../../components/theme/index.jsx";
import { toggleButtonStyle } from "../../consts.js";
import { DATE_FORMAT, INSIGHT_PAGE, THEME_MODES } from "../../helpers/constant.js";
import { capitalize, getComparatorSort, removeDetectKeywordFromVisuals } from "../../helpers/index.js";
import { ConversationInsightModel } from "../../models/conversation.js";
import { getInsightDataAPI } from "../../services/analytics.js";
import VIcon from "../images/Logo_black.png";
import Loader from "../../components/loader/index.jsx";

function ConversationInsight({ conversationId }) {
    const { colours, themeMode, FONT } = useThemeMode();
    const AppDataContextDTO = useContext(AppDataContext);
    const [spinner, setSpinner] = React.useState(false);
    const [pickedAgent, setPickedAgent] = React.useState("agent");
    const [pickedInsightAgent, setPickedInsightAgent] = React.useState("Agent");

    const [analytics, setAnalytics] = useState(new ConversationInsightModel());

    // Fetch conversation data with error handling
    async function fetchConversationData() {
        try {
            setSpinner(true);
            const { data } = await getInsightDataAPI({ id: conversationId, type: INSIGHT_PAGE.CONVERSATION });
            data.topics.forEach(t => {
                if (!t.messageIds) {
                    t.messageIds = [t.text]
                }
            });
            setAnalytics(Object.assign(analytics, data));
            // set data for breadcrumb
            setSpinner(false);
            try {
                AppDataContextDTO.setCampaign({ id: data.campaign_data.id, name: data.campaign_data.name });
            } catch (error) { }
        } catch (error) {
            console.log(error);
            setSpinner(false);
        }
    }

    React.useEffect(() => {
        setAnalytics(new ConversationInsightModel());
        setPickedAgent("agent");
        setPickedInsightAgent("Agent");
        fetchConversationData();
    }, [conversationId]);

    // Cards Data
    const cardsData = [
        {
            icon: "user",
            title: "Agent",
            value: analytics?.agent,
            backgroundColor: colours.blue10,
            borderColor: colours.blue20,
            iconBg: colours.blue100,
        },
        {
            icon: "calendar",
            title: "Date",
            value: analytics?.conversation_date ? format(new Date(analytics?.conversation_date), DATE_FORMAT) : "",
            backgroundColor: colours.purple5,
            borderColor: colours.purple20,
            iconBg: colours.purple100,
        },
        {
            icon: "watch",
            title: "Call Duration",
            value: analytics.duration,
            backgroundColor: colours.success5,
            borderColor: colours.success20,
            iconBg: colours.success100,
        },
        {
            customAvatar: <Avatar sx={(theme) => ({ bgcolor: theme.palette.mode === THEME_MODES.LIGHT ? colours.white : colours.black })}><img src={VIcon} alt="Vocalysd Avatar" loading="lazy" width={18} height={22} /></Avatar>,
            title: "Avg. Vocalysd Score",
            customValue: <Typography variant="h5" component="div" fontWeight={600} color={colours.success100}>{analytics.VS || 0}%</Typography>,
            backgroundColor: colours.orange10,
            borderColor: colours.orange20,
        },
    ];

    // Determines color based on sentiment
    const getSentimentColor = (item) => {
        if (item?.sentiment === "positive") return "success";
        if (item?.sentiment === "negative") return "warning";
        return "inherit";
    };

    return (
        <Grid
            className="vocalysed-home-upadted conversation-insights"
            container
            spacing={0}
            direction="row"
            flexGrow={1}
            padding={3}
            gap={3}
            overflow="auto"
            alignItems="start"
            sx={{ overflowX: "hidden" }}
        >
            {spinner && <Loader />}
            {/* Top Cards Section */}
            <Grid
                item
                xs={12}
                padding={3}
                sx={{ border: `1px solid ${colours.grayDark500}`, borderRadius: 2.5, boxShadow: "none", backgroundColor: colours.white }}
                component={Paper}
            >
                <Grid
                    container
                    spacing={3}
                    direction="row"
                >
                    {cardsData.map((card, index) => (
                        <Grid key={index} item xs={12} sm={6} lg={3}>
                            <InfoCardBox
                                colours={colours}
                                themeMode={themeMode}
                                icon={card.icon}
                                title={card.title}
                                tooltip={card.tooltip}
                                value={card.value}
                                backgroundColor={card.backgroundColor}
                                borderColor={card.borderColor}
                                iconBg={card.iconBg}
                                customAvatar={card.customAvatar}
                                customValue={card.customValue}
                            />
                        </Grid>
                    ))}
                    {analytics?.summary?.length > 0 &&
                        <Grid item xs={12}>
                            <Typography variant="body2" fontWeight={600} gutterBottom>Conversation Summary</Typography>
                            <Typography variant="body2" sx={{ fontSize: FONT.SIZE_13 }}>
                                <AppSeeMoreLess clampLines={2}>
                                    {analytics.summary}
                                </AppSeeMoreLess>
                            </Typography>
                        </Grid>
                    }
                </Grid>
            </Grid>
            <Grid
                container
                spacing={0}
                direction="row"
                gap={3}
            >
                <Grid item xs={12} lg sx={{ minWidth: "1px" }} display="flex" flexDirection="column" gap={3}>
                    <Grid
                        container
                        flexWrap={{ sm: "nowrap" }}
                        justifyContent="space-between"
                        gap={3}
                    >
                        <Paper
                            sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                        >
                            <CardHeaderTitle
                                icon="smile"
                                title="Sentiment"
                                tooltip="Sentiment graph showing the overall sentiment trends for this campaign"
                            />
                            <Box marginTop={2} height={150}>
                                <AppBarChart data={analytics.sentiment} />
                            </Box>
                        </Paper>
                        <Paper
                            sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                        >
                            <CardHeaderTitle
                                icon="alert-triangle"
                                title="Alerts"
                                tooltip="Campaign alerts showing critical updates and notifications"
                            />
                            {(!analytics.alerthits?.length) ? (<Stack fontSize={FONT.SIZE_14} height="100%" alignItems="center" justifyContent="center">
                                No Data Available
                            </Stack>) :
                                (<Box marginTop={2}>
                                    <Stack direction="row" flexWrap="wrap" gap={1}>
                                        {analytics.alerthits.length > 0
                                            ? analytics.alerthits.map((alert, index) => (
                                                <Chip
                                                    key={index}
                                                    label={removeDetectKeywordFromVisuals(alert)}
                                                    size="small"
                                                    color="warning"
                                                />
                                            ))
                                            : null // Fallback message if alerts is empty or undefined
                                        }
                                    </Stack>
                                </Box>)}
                        </Paper>
                    </Grid>
                    <Paper
                        sx={{ display: "flex", flexDirection: "column", width: '100%', borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                    >
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={.5}
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <CardHeaderTitle
                                icon="activity"
                                title="Insights"
                                tooltip="Insights highlighting key phrases, topics, and trends detected in agent-client interactions"
                            />
                            <ButtonGroup
                                variant="contained"
                                aria-label="outlined primary button group"
                                className="buttons-border-extend"
                            >
                                <Button
                                    style={toggleButtonStyle(pickedInsightAgent === "Agent", colours, themeMode)}
                                    onClick={() => setPickedInsightAgent("Agent")}
                                >
                                    Agent
                                </Button>
                                <Button
                                    style={toggleButtonStyle(pickedInsightAgent === "Customer", colours, themeMode)}
                                    onClick={() => setPickedInsightAgent("Customer")}
                                >
                                    Client
                                </Button>
                            </ButtonGroup>
                        </Stack>
                        <ProgressTable
                            data={_.filter((analytics?.trackers_detected || []), { "for": pickedInsightAgent })}
                            filterKey="name"
                            sortKey="matches"
                            maxItems={20}
                            capCase={(val) => capitalize(removeDetectKeywordFromVisuals(val))}
                            getColorFn={() => "warning"}
                            getValueFn={(item) => item.matches.length}
                        />
                    </Paper>
                    <Paper
                        sx={{ display: "flex", flexDirection: "column", width: '100%', borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                    >
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={.5}
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <CardHeaderTitle
                                icon="check-circle"
                                title="Top Questions"
                                tooltip="Top questions identified from campaign conversations, based on frequency"
                            />
                            <ButtonGroup
                                variant="contained"
                                aria-label="outlined primary button group"
                                className="buttons-border-extend"
                            >
                                <Button
                                    onClick={() => setPickedAgent('agent')}
                                    style={toggleButtonStyle(pickedAgent === 'agent', colours, themeMode)}
                                >
                                    Agent
                                </Button>
                                <Button
                                    onClick={() => setPickedAgent('client')}
                                    style={toggleButtonStyle(pickedAgent === 'client', colours, themeMode)}
                                >
                                    Client
                                </Button>
                            </ButtonGroup>
                        </Stack>
                        <Box marginTop={2}>
                            <TableContainer>
                                <Table size="small" aria-label="Top Questions Table">
                                    <TableBody>
                                        {analytics.questions[pickedAgent].sort(getComparatorSort('desc', 'text'))
                                            .sort(getComparatorSort('asc', 'n'))
                                            .slice(0, 10)
                                            .map((z, zi) => (
                                                <TableRow hover key={zi}>
                                                    <TableCell
                                                        style={{
                                                            paddingTop: 3,
                                                            paddingBottom: 3,
                                                        }}
                                                    >
                                                        {zi + 1 + ". " + z.text}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        {!analytics.questions[pickedAgent]?.length ? (
                                            <TableRow>
                                                <TableCell colSpan={3}>
                                                    No Data Available
                                                </TableCell>
                                            </TableRow>
                                        ) : ("")}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} lg sx={{ minWidth: "1px" }} display="flex" flexDirection="column" gap={3}>
                    <Grid
                        container
                        flexWrap={{ sm: "nowrap" }}
                        justifyContent="space-between"
                        gap={3}
                    >
                        <Paper
                            sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                        >
                            <CardHeaderTitle
                                icon="check-circle"
                                title="Avg. WPM Agent vs Client"
                                tooltip="Average words per minute (WPM) comparison between agent and client in this campaign"
                            />
                            <Box marginTop={1} height={150}>
                                <WPMChartComponent data={analytics.wpm} />
                            </Box>
                        </Paper>
                        <Paper
                            sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                        >
                            <CardHeaderTitle
                                icon="pie-chart"
                                title="Speaker Analytics"
                                tooltip="Speaker analytics providing insights on individual speaker performance"
                            />
                            <Box height={166}>
                                <AppPieChart data={analytics.speaker_analysis} />
                            </Box>
                        </Paper>
                    </Grid>

                    <Paper
                        sx={{ display: "flex", flexDirection: "column", width: '100%', borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                    >
                        <CardHeaderTitle
                            icon="message-circle"
                            title="Topics"
                            tooltip="The top topics discussed in this campaign based on conversation analysis"
                        />
                        <ProgressTable
                            data={analytics?.topics?.filter((r) => r.text) || []}
                            filterKey="text"
                            sortKey="messageIds"
                            maxItems={10}
                            capCase={capitalize}
                            getValueFn={(item) => (item.messageIds?.length) || 0}
                            getColorFn={getSentimentColor}
                        />
                    </Paper>
                </Grid>
            </Grid>
        </Grid >
    );
}

export default ConversationInsight;